/**
 * Role-aware navigation (§3 → §16.2 consoles). Visibility is a permission question answered by `can()` from
 * contracts; a sponsor viewer holds only `sponsor.report.view` and therefore sees only Sponsor reporting.
 *
 * Sections group by job-to-be-done (D-2602): Run (dashboard, lotteries, draw room, reports) · Participants
 * (winners, claims, support, fulfillment, engagement review) · Money (finance) · Audience & sponsors (audience,
 * catalogue, sponsor reporting) · Govern (audit, settings). Permission keys are unchanged.
 */
import type { Permission, StaffRole, StaffSessionDto } from '@windrop/contracts';
import { sessionCan } from '../auth/session';

export type NavSection = 'run' | 'participants' | 'money' | 'audience' | 'govern';

export interface NavItem {
  id: string;
  label: string;
  to: string;
  /** ANY of these permissions shows the item. */
  permissions?: readonly Permission[];
  /** ANY of these roles shows the item (for areas without a dedicated permission, e.g. staff settings). */
  roles?: readonly StaffRole[];
  /** Section grouping in the sidebar. */
  section: NavSection;
  /** Keyboard shortcut (g then key). */
  shortcut?: string;
  /** One-line purpose, shown in the command palette. */
  hint?: string;
}

export const NAV_ITEMS: readonly NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    to: '/',
    permissions: ['lottery.read'],
    section: 'run',
    shortcut: 'd',
    hint: 'Live funnel, SLAs, liability and exceptions',
  },
  {
    id: 'lotteries',
    label: 'Lotteries',
    to: '/lotteries',
    permissions: ['lottery.read'],
    section: 'run',
    shortcut: 'l',
    hint: 'Every lottery with its lifecycle state',
  },
  {
    id: 'draws',
    label: 'Draw control room',
    to: '/draws',
    permissions: ['draw.precheck', 'draw.run', 'draw.reveal', 'draw.audit.read'],
    section: 'run',
    shortcut: 'r',
    hint: 'Gates, two-person controls, reveal, fairness export',
  },
  {
    id: 'reports',
    label: 'Reports',
    to: '/reports',
    permissions: ['lottery.read'],
    section: 'run',
    hint: 'Next business day, hypercare and campaign close',
  },
  {
    id: 'winners',
    label: 'Winners board',
    to: '/winners',
    permissions: ['winners.view'],
    section: 'participants',
    shortcut: 'w',
    hint: 'Messaging state, assignment, win-ID lookup',
  },
  {
    id: 'claims',
    label: 'Claims queue',
    to: '/claims',
    permissions: ['claims.view'],
    section: 'participants',
    shortcut: 'c',
    hint: 'Age, deadline, missing items, verification',
  },
  {
    id: 'support',
    label: 'Support cases',
    to: '/support',
    permissions: ['claims.view'],
    section: 'participants',
    hint: 'Priority, owner and next update',
  },
  {
    id: 'fulfillment',
    label: 'Fulfillment',
    to: '/fulfillment',
    permissions: ['fulfillment.update'],
    section: 'participants',
    hint: 'Dispatch SLA, exceptions, delivery evidence',
  },
  {
    id: 'engagement',
    label: 'Engagement review',
    to: '/engagement',
    permissions: ['engagement_tasks.manage'],
    section: 'participants',
    hint: 'Task completions awaiting review',
  },
  {
    id: 'finance',
    label: 'Finance',
    to: '/finance',
    permissions: ['finance.ledger.read', 'finance.reconcile', 'finance.refund', 'finance.payout.approve'],
    section: 'money',
    shortcut: 'f',
    hint: 'Reconciliation, refunds, payouts, ledger',
  },
  {
    id: 'audience',
    label: 'Audience',
    to: '/audience',
    permissions: ['audience.segments.read'],
    section: 'audience',
    shortcut: 'u',
    hint: 'Profiles, segments, exports, engagement',
  },
  {
    id: 'catalog',
    label: 'Sponsors & prizes',
    to: '/catalog',
    permissions: ['prizes.manage', 'sponsors.manage', 'brands.manage'],
    section: 'audience',
    shortcut: 'p',
    hint: 'Products, prizes, brands and sponsors',
  },
  {
    id: 'sponsor-reports',
    label: 'Sponsor reporting',
    to: '/sponsor-reports',
    permissions: ['sponsor.report.view'],
    section: 'audience',
    shortcut: 's',
    hint: 'Placements, samples and branded exports',
  },
  {
    id: 'audit',
    label: 'Audit & incidents',
    to: '/audit',
    permissions: ['audit.read'],
    section: 'govern',
    shortcut: 'a',
    hint: 'Append-only evidence, incidents, access grants',
  },
  {
    id: 'settings',
    label: 'Settings & staff',
    to: '/settings',
    roles: ['ORGANIZER_ADMIN', 'PLATFORM_SUPER_ADMIN'],
    section: 'govern',
    hint: 'Staff roster, invitations and roles',
  },
];

export const SECTION_ORDER: readonly NavSection[] = ['run', 'participants', 'money', 'audience', 'govern'];

export const SECTION_LABELS: Record<NavSection, string> = {
  run: 'Run',
  participants: 'Participants',
  money: 'Money',
  audience: 'Audience & sponsors',
  govern: 'Govern',
};

export const isNavItemVisible = (item: NavItem, session: StaffSessionDto | null): boolean => {
  if (!session) return false;
  const byPermission = item.permissions?.some((p) => sessionCan(session, p)) ?? false;
  const byRole = item.roles?.some((r) => session.roles.includes(r)) ?? false;
  return byPermission || byRole;
};

export const visibleNav = (session: StaffSessionDto | null): NavItem[] =>
  NAV_ITEMS.filter((item) => isNavItemVisible(item, session));

/** Where `/` should land for this session: the dashboard when allowed, else the first visible console. */
export const homePath = (session: StaffSessionDto | null): string => {
  const items = visibleNav(session);
  if (items.some((i) => i.id === 'dashboard')) return '/';
  return items[0]?.to ?? '/forbidden';
};
